import { Toolbar } from "@mui/material";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Sankey } from "react-vis";
import { SimpleSelect } from "../../../../../common/components/input/Select";
import {
  useDataMapCollected,
  useDataMapCollectedPICGroups,
  useDataMapDisclosed,
  useDataMapDisclosedPICGroups,
} from "../../../../../common/service/resources/data-map/dataMapTransformers";
import { DataMapDto } from "../../../../../common/service/server/dto/datamap/DataMapDto";
import { LEGACY_CONTRACTOR_CATEGORY_SENTINEL } from "../../../../util/vendors";

// This needs to be something highly unlikely to appear in a vendor category name.
const GROUP_KEY_SEPARATOR = "[###]";

type SankeyDataMapProps = {
  datamap: DataMapDto;
};
export const SankeyDataMap: React.FC<SankeyDataMapProps> = ({ datamap }) => {
  const collectionGroups = Object.values(datamap.collectionGroups).filter(
    (g) => g.type !== "EMPLOYMENT",
  );
  const collectionGroupIds = collectionGroups.map((cg) => cg.id);

  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    setWidth(ref.current.offsetWidth);
  }, []);

  const [activeLink, setActiveLink] = useState(null);
  const [collectionGroupId, setCollectionGroupId] = useState(collectionGroupIds[0]);

  const disclosedPICGroups = useDataMapDisclosedPICGroups(datamap, {
    collectionGroupId: collectionGroupId,
  });
  const collectedPICGroups = useDataMapCollectedPICGroups(datamap, {
    collectionGroupId: collectionGroupId,
  });

  const disclosed = useDataMapDisclosed(datamap, { collectionGroupId: collectionGroupId });
  const collection = useDataMapCollected(datamap, { collectionGroupId: collectionGroupId });

  const picGroupNodes = [
    ...disclosedPICGroups.map((g) => ({ name: g.name, key: g.id, color: "#BBADEB" })),
    ...collectedPICGroups.map((g) => ({ name: g.name, key: g.id, color: "#BBADEB" })),
  ];
  const categoryNodes = _.uniqBy(
    picGroupNodes.map((g) => ({ name: g.name, key: g.key, color: "#BBADEB" })),
    "key",
  );

  const group = datamap?.collectionGroups[collectionGroupId];
  const collectionGroupNodes = group ? [{ name: group.name, key: group.id, color: "#BBADEB" }] : [];

  const vendorCategoryNodes = _.uniqBy(
    disclosed.map((d) => {
      const v = datamap.recipients[d.vendorId];
      return {
        name: v.category,
        key: v.category,
        color: "#BBADEB",
      };
    }),
    "key",
  );

  const dmNodes = [...categoryNodes, ...collectionGroupNodes, ...vendorCategoryNodes].map(
    (obj) => ({
      ...obj,
      name: obj.name === LEGACY_CONTRACTOR_CATEGORY_SENTINEL ? "Contractor" : obj.name,
    }),
  );

  const groupedCollection = _.groupBy(collection, (c) => {
    const picGroupId = datamap.categories[c.picId]?.picGroupId;
    return `${picGroupId}!${c.groupId}`;
  });
  const collectionLinks = Object.entries(groupedCollection).map(([groupKey, collection]) => {
    const [picGroupId, collectionGroupId] = groupKey.split("!");
    return {
      source: _.findIndex(dmNodes, (n) => n.key === collectionGroupId),
      target: _.findIndex(dmNodes, (n) => n.key === picGroupId),
      color: "#e8e5ee",
      value: collection.length,
    };
  });

  const groupedDisclosure = _.groupBy(disclosed, (d) => {
    const picGroupId = datamap.categories[d.picId]?.picGroupId;
    const vendorCategory = datamap.recipients[d.vendorId].category;
    return `${picGroupId}${GROUP_KEY_SEPARATOR}${vendorCategory}`;
  });
  const disclosureLinks = Object.entries(groupedDisclosure).map(([groupKey, disclosure]) => {
    const [picGroupId, vendorCategory] = groupKey.split(GROUP_KEY_SEPARATOR);
    return {
      source: _.findIndex(dmNodes, (n) => n.key === picGroupId),
      target: _.findIndex(dmNodes, (n) => n.key === vendorCategory),
      color: "#e8e5ee",
      value: disclosure.length,
    };
  });

  const uniqueCollectionLinks = _.uniqBy(collectionLinks, (l) => `${l.source}-${l.target}`);
  const uniqueDisclosureLinks = _.uniqBy(disclosureLinks, (l) => `${l.source}-${l.target}`);

  const links = [...uniqueCollectionLinks, ...uniqueDisclosureLinks];
  const collectionGroupOptions = collectionGroups.map((cg) => ({ value: cg.id, label: cg.name }));

  return (
    <div ref={ref}>
      {collectionGroupOptions.length > 1 && (
        <Toolbar className="mb-md">
          <SimpleSelect
            className="ml-auto"
            size="small"
            options={collectionGroupOptions}
            value={collectionGroupId}
            onChange={(e) => setCollectionGroupId(e.target.value as string)}
          />
        </Toolbar>
      )}

      {width > 0 && (
        <Sankey
          animation
          align={"center"}
          nodes={dmNodes}
          links={links.map((link, i) => {
            return {
              ...link,
              color: activeLink && i === activeLink.index ? "#BBADEB" : "#e8e5ee",
            };
          })}
          width={width}
          height={400}
          layout={24}
          style={{
            links: {
              opacity: 0.1,
            },
            labels: {
              fontFamily: "Roboto",
              fontWeight: "bold",
              fontSize: 11,
            },
          }}
          onLinkMouseOver={(node) => setActiveLink(node)}
          onLinkMouseOut={() => setActiveLink(null)}
        />
      )}
    </div>
  );
};
